// Frontend registry of runtime feature flags (#842). Mirrors KNOWN_FLAGS in
// cloud_functions/profile_manager_v2/feature_flags.py — the backend is the
// authority (it rejects unknown flags); this list just drives the admin card
// UI and gives each flag a human title.
import { PATENT_APPLICATION_USD } from '../constants/patentLabPricing';
import { SEASON_PASS_REGULAR_USD, THROUGH_GRADUATION_USD, THROUGH_GRADUATION_INR } from '../constants/seasonPricing';

// Two flags remained after the graduated ones were retired (#3615, founder
// 2026-10-09: "if features are already live, remove the feature flag calls").
// Every other feature that once rode a flag is simply on. astra_today (#3752)
// and aid_track (#4154) are the rollout gates added since; retire each once
// it is on for everyone.
export const FEATURE_FLAGS = [
    // KILL SWITCH, not a rollout: Off closes every signed-out Astra surface
    // (the landing chat and free read, the college-page and News Ask boxes)
    // at once; the server refuses the route. Kept because that surface is
    // the public, unauthenticated LLM spend and abuse exposure.
    {
        key: 'astra_lite',
        title: 'Astra for signed-out visitors (kill switch)',
        description:
            'Everyone: signed-out visitors meet Astra on the landing page (the '
            + 'open chat and the free read) and in the Ask boxes on college and '
            + 'News pages, with every Astra guardrail and the free daily limit. '
            + 'Off: the whole signed-out Astra surface closes and the page falls '
            + 'back to the plain free read. Just us opens it only for listed '
            + 'signed-in accounts.',
    },
    // The Patent Lab application add-on (founder 2026-09-22; repriced
    // 2026-09-23: one included with the Season Pass, then the price in
    // constants/patentLabPricing.js). Off: the formal specification is free
    // to Season Pass holders.
    {
        key: 'patent_billing',
        title: 'Patent application billing',
        description:
            'Charge the patent application at "Write the specification": '
            + `one included with the Season Pass, then $${PATENT_APPLICATION_USD} `
            + 'per invention, Season Pass holders only. '
            + 'Off until counsel answers ADR 0012.',
    },
    // Astra Today (#3752, ADR 0041; founder pick 2026-10-10: A1, B2, C1, D).
    // A rollout gate for the pilot: Just us = the internal and demo accounts.
    // Widening it to real students is the founder's call.
    {
        key: 'astra_today',
        title: 'Astra Today (next steps, notifications)',
        description:
            'Astra keeps one to three next steps for each student: sign-in lands '
            + 'on /today, Astra\'s row and pill say "1 thing for you", web push '
            + 'after a well-timed yes, and the Notes from Astra settings card. '
            + 'Just us: the pilot accounts only. Off: none of it shows and the '
            + 'nightly run and the push sender skip everyone.',
    },
    // Paying for college (#4154, founder pick B 2026-10-13). A rollout gate:
    // Just us = internal and demo accounts. Retire once it is on for everyone.
    {
        key: 'aid_track',
        title: 'Paying for college (FAFSA and CSS Profile steps)',
        description:
            'Seniors see a numbered card above the Roadmap phases: StudentAid.gov '
            + 'accounts, documents, the FAFSA, the CSS Profile with each list '
            + 'school\'s aid dates from its own page, the Submission Summary, and '
            + 'New York\'s TAP for New York residents. A parent sees it on the share '
            + 'link. Off: the card, the aid dates it adds to the ribbon and This '
            + 'Week, and Astra\'s aid read are hidden. The FAFSA row\'s date fix '
            + '(dated by your schools, never by its opening day) holds either way.',
    },
    // Profile-first sign-up (#4702, founder approval 2026-10-18). A rollout
    // gate: Just us = internal and demo accounts. Retire once it is on for
    // everyone.
    {
        key: 'profile_first_signup',
        title: 'Profile-first sign-up (three facts, three colleges)',
        description:
            'A new student\'s first screen (/start) asks their GPA, a test score or '
            + 'none, an intended major and three colleges, nothing else first, then '
            + 'reads the three schools in the same session. Off: today\'s /welcome '
            + '(phone) and /profile (desktop) first steps.',
    },
    // Onboarding that ends with the first list read (#5085, founder pick A
    // 2026-10-21). A rollout gate: Just us = internal and demo accounts.
    // Retire once it is on for everyone.
    {
        key: 'first_read_onboarding',
        title: 'First read in onboarding (one college first)',
        description:
            '/start asks for one college first, then GPA, a test score and a major '
            + 'on one short screen, and reads that school in about half a minute; '
            + 'two more can be added right under the read. A phone with a typed '
            + 'landing draft lands on /start too. Off: today\'s /start (three '
            + 'colleges, then the read) and /welcome for a typed draft on a phone.',
    },
    // The 7-day reverse trial (#4703, founder approval 2026-10-18). A rollout
    // gate: Just us = internal and demo accounts. Retire once it is on for
    // everyone.
    {
        key: 'reverse_trial',
        title: '7 days of everything (reverse trial, no card)',
        description:
            'A new student gets the full Season Pass for 7 days from their first '
            + 'list read; an existing free account gets its 7 days at its next '
            + 'sign-in, once. No card, nothing converts. After day 7 the account is '
            + 'Free again and the first new deep request shows the Season Pass offer '
            + '(Buy, or Send to a parent). Off: nobody starts a trial; trials already '
            + 'running finish their 7 days.',
    },
    // The parent read and the day-5 email (#4704, founder approval
    // 2026-10-18). A rollout gate: Just us = internal and demo accounts.
    {
        key: 'parent_read',
        title: 'The parent read (and the day-5 email)',
        description:
            'A student\'s share link to a parent opens one page: the list read '
            + 'school by school with each school\'s sourced numbers, deadlines and '
            + 'cost, what full-service consulting costs, the 30-day money-back, and a Buy the '
            + 'parent pays with their own card. Everyone (with the 7-day trial on '
            + 'for everyone too) also starts the day-5 email. Off: the ordinary '
            + 'review page.',
    },
    // Open the parent path (#5811, founder pick A, 2026-10-27). A rollout
    // gate: Just us = internal and demo accounts.
    {
        key: 'parent_path',
        title: 'The parent path',
        description:
            '/start asks who the account is for: a student, or a parent or guardian '
            + '(one tap, with a skip). A parent\'s home is the parent read of their own '
            + 'list, with the Season Pass. "Send this to a parent" is one tap under a '
            + 'read, under the cost chart and on the aid index, through a sheet that '
            + 'shares the list only, so the parent\'s page needs no code and shows the '
            + 'Buy. Settings gets the switch. Off: none of it; sends go to /share.',
    },
    // The trial's day-5 email (#4704): a held sender's switch, not a rollout.
    {
        key: 'trial_day5_email',
        title: 'The trial\'s day-5 email',
        description:
            '"Your reads pause in 2 days. Send this to a parent." Everyone: the Desk '
            + 'sends it once to each student on day 5 of the 7-day trial, through the '
            + 'shared audience, the daily cap and the ledger, only while the trial is '
            + 'on for everyone and offers are enabled (they wait for the postal '
            + 'address). Anything else holds it.',
    },
    // /pricing as one plan (#4705, founder approval 2026-10-18). A rollout
    // gate: Just us = internal and demo accounts; Everyone includes
    // signed-out visitors. Retire once it is on for everyone.
    {
        key: 'pricing_one_plan',
        title: 'Pricing: one plan, one sentence',
        description:
            '/pricing opens on "Everything for your whole season." with the Season '
            + 'Pass at its current price, what full-service consulting charges (with its '
            + 'source), the refund and its condition, fair use and the renewal, and '
            + 'one line for Free. Off: today\'s two-card page.',
    },
    // India visitors and the master's abroad work (#4967, epic #4962). A
    // rollout gate: Just us = internal and demo accounts (signed in; add
    // ?region=in to the landing page to see it from outside India);
    // Everyone includes signed-out visitors. Retire once it is on for
    // everyone.
    {
        key: 'india_masters',
        title: 'India visitors: Master\'s abroad waitlist and rupee pricing',
        description:
            'Visitors whose browser time zone is India see a Master\'s abroad section '
            + 'on the landing page with a waitlist (computer science, data science, '
            + 'ECE, business analytics), and the sign-ups count on the Desk. They also '
            + 'see the Season Pass at Rs 9,999 on /pricing, the upgrade modal and the '
            + 'trial wall, with a switch to US dollars; checkout holds the rupee charge '
            + 'and takes it only from a card issued in India (any other card is released, '
            + 'nothing charged). GST is on hold: keep this off for real buyers. Just us: '
            + 'the listed accounts, with ?region=in to preview from anywhere. Off: no '
            + 'section, dollars everywhere, and the waitlist and pricing routes refuse.',
    },
    // Comments on a shared link (#4987, ADR 0062; founder pick A, 2026-10-21).
    // A rollout gate for the student who shared. Retire once on for everyone.
    {
        key: 'share_comments',
        title: 'Comments on a shared link',
        description:
            'A counselor or parent whose link the student opened to comments leaves '
            + 'a note under a school, an essay or a section, after the email code and '
            + 'with their name. The student sees a badge on that school or essay, '
            + 'replies and resolves from Sharing, and the reply shows on the link. '
            + 'Just us: the listed students. Off: no comments anywhere, the routes '
            + 'refuse, and notes already written stay stored.',
    },
    // One-payment passes (#4986, founder 2026-10-21). A rollout gate: Just us
    // = internal and demo accounts (signed in); Everyone includes signed-out
    // visitors. Retire once it is on for everyone.
    {
        key: 'one_payment_passes',
        title: 'One-payment passes: Season Pass without renewal, and Through Graduation',
        description:
            `A new Season Pass is $${SEASON_PASS_REGULAR_USD}, one payment for a year, and does not renew; `
            + '/pricing, the upgrade modal, the parent read and the Terms say so. Grades 9 to 12 are also '
            + `offered the Season Pass through graduation, $${THROUGH_GRADUATION_USD} once (₹${THROUGH_GRADUATION_INR.toLocaleString('en-IN')} `
            + 'in India while india_masters is on, for an India-issued card), through June 30 of '
            + 'their graduation year or a year from purchase, whichever is later (the server works the year out '
            + 'from the profile; a senior runs a year from purchase). '
            + 'Existing subscriptions keep renewing on their own terms. Off: today\'s renewing Pass, no '
            + 'Through Graduation, and its checkout and offer refuse.',
    },
    // Sign-in on phones (#5084, founder pick A, 2026-10-21). A rollout gate
    // for a signed-out surface: only Everyone reaches it (get-auth-methods
    // serves it); the team previews with ?signin_preview=phone. Retire once
    // it is on for everyone.
    {
        key: 'phone_email_first',
        title: 'Sign-in on phones: email first on iPhone and iPad Safari',
        description:
            'On iPhone and iPad Safari and the Home Screen app, the sign-in sheet leads with the '
            + 'email field and "Email me a sign-in link", with Continue with Google as a full button '
            + 'under it, and a Google sign-in that did not finish comes back with the field showing. '
            + 'Chrome, Android and desktop keep today\'s sheet. Visitors are signed out, so only '
            + 'Everyone reaches them; Just us does nothing here. Preview it on a team device with '
            + '?signin_preview=phone. Off: today\'s Google-first sheet.',
    },
    // More ways to sign in (#5154, founder pick A, 2026-10-22). A rollout
    // gate for a signed-out surface: only Everyone reaches it
    // (get-auth-methods serves it); the team previews with
    // ?signin_preview=more. Each provider also needs the founder's own setup
    // in Firebase Auth. Retire once it is on for everyone.
    {
        key: 'more_sign_in_options',
        title: 'Sign-in: Continue with Apple (and Facebook)',
        description:
            'The sign-in sheet adds Continue with Apple beside Continue with Google, all the same '
            + 'size: Apple, Google on iPhones and iPads, Google first on Android and desktop. Facebook '
            + 'is built and held until its email question is settled. A provider shows only once the '
            + 'founder has set it up in Firebase Auth. Visitors are signed out, so only Everyone '
            + 'reaches them; Just us does nothing here. Preview it on a team device with '
            + '?signin_preview=more. Off: today\'s sheet.',
    },
    // The landing page told as one story (#5128, founder pick A of #5093). A
    // rollout gate for a signed-out, prerendered page: only Everyone reaches
    // it, and going live also needs a frontend deploy (the prerender reads
    // the public flags at build time); the team previews with
    // ?landing_preview=1. Retire once it is on for everyone, with the old order.
    {
        key: 'landing_story',
        title: 'Landing page: one story, top to bottom',
        description:
            'The home page leads with "Start with your story. See where it fits." over Ask '
            + 'Astra, then the quotes, three steps, Look up a college, everything the plan covers, '
            + 'the builder tools as one band, the counselor card, what it costs (the Season Pass '
            + 'price) and one line to the tour, with a new page title and Google description. '
            + 'Visitors are signed out, so only Everyone reaches them; Just us does nothing here. '
            + 'Everyone also needs a frontend deploy so the prerendered page carries it. Preview it '
            + 'on a team device with ?landing_preview=1. Off: today\'s landing page.',
    },
    // The ranker for the next Shorts and tutorials (#5156, founder pick A).
    // Desk-only: Just us is the shadow week (picks shown, nothing built from
    // them); Everyone builds the empty Short places from the picks. Retire
    // once it is on for everyone.
    {
        key: 'desk_ranker',
        title: 'Desk: rank the next Shorts and tutorials from every number',
        description:
            'The Desk ranks the next Shorts and tutorials from Astra Lite questions, search '
            + 'impressions, page readers, each Short\'s views, watch and likes, visits, comment '
            + 'requests and coverage. Just us: the shadow week, picks shown on the Daily posts card, '
            + 'a Monday Next tutorials card and Growth > Next up, while every build still takes the '
            + 'list. Everyone: the empty Short places build from the picks. Off: none of it.',
    },
    // Tell the Desk (#5155, founder pick A): the Desk's request box and its
    // Requests tab. A rollout gate on the founder's own Desk; retire once on.
    {
        key: 'desk_requests',
        title: 'Tell the Desk: one box for your requests',
        description:
            'A box on the Desk, on Today and a new Requests tab, for anything you want the '
            + 'Desk to do. Each request gets an id; your Claude session picks it up first and '
            + 'the Mac after 10 minutes, answers on its thread, and every card, issue and PR it '
            + 'spawns links back. Anything that spends, sends or publishes still comes back as '
            + 'a card. Desk only: no student sees it. Just us with your email listed turns it '
            + 'on for you. Off: no box, no tab.',
    },
    // Admin holds Operations and Settings (#5186, founder pick A of #5179).
    // A rollout gate on the founder's own console; retire once on, with the
    // old pages and nav entries.
    {
        key: 'admin_under_operations',
        title: 'Admin: only Operations and Settings',
        description:
            'The Knowledge Base page becomes Desk > Knowledge base (Coverage, Collect and Student '
            + 'searches beside Daily check and Fact ledger), Feature flags a sub-tab of System '
            + 'health, and Feedback and Credits sub-tabs of Customers. Every old address opens the '
            + 'new place. Admin only: no student sees it. Just us with your email listed turns it '
            + 'on for you. Off: the six Admin entries and their pages, as before.',
    },
    // Sign-up moments (#5195, founder pick A of the mocks). A rollout gate for
    // signed-out pages: only Everyone reaches visitors, and going live also
    // needs a frontend deploy (the college pages are prerendered with the
    // public flags); the team previews with ?signup_preview=1. Retire once it
    // is on for everyone, with today's prompts.
    {
        key: 'signup_moments',
        title: 'Sign-up moments: ask right under the value',
        description:
            'Under a guest\'s Where do I stand result and the home chat\'s free read: a line when a '
            + 'school reads the same band at every GPA, then where their GPA reads Target ("Target at '
            + '36 colleges we cover, like ...") with a button that names it. A "See your chances here" '
            + 'block halfway down a college page, a "Your colleges" strip on the second college page of '
            + 'a visit, and the sign-in sheet repeating the button\'s words. Visitors are signed out, so '
            + 'only Everyone reaches them; Just us does nothing here. Everyone also needs a frontend '
            + 'deploy so the prerendered college pages carry it. Preview it on a team device with '
            + '?signup_preview=1. Off: today\'s prompts.',
    },
    // Popular questions inside Astra (#5258, founder pick A of the #5243
    // mocks). A rollout gate: counselor-agent serves the rows; this app
    // reads it for the Playbook's retirement. Retire once on for everyone.
    {
        key: 'astra_popular_questions',
        title: 'Popular questions inside Astra (the Playbook folded in)',
        description:
            'Both Astra chats open with up to three tap-to-ask questions for the topics asked most '
            + 'this week, ranked by counting, never a model. Signed out: a "Most asked this week" row '
            + 'and one plain line under it. Signed in: "Students are asking" under the student\'s own '
            + 'next steps. The Playbook page retires: its nav item goes and /command-center opens '
            + 'Connect\'s new "Prompts for your agent", Ask Astra first. Just us shows it to listed '
            + 'accounts signed in (and preview links). Off: today\'s chats and the Playbook.',
    },
    // The connector's own sign-in and consent (#5259, founder pick A). Read
    // by stratia_connector (feature_gate twin). Retire once on for everyone.
    {
        key: 'connector_signin_v2',
        title: 'Connect Stratia: its own sign-in and consent screen',
        description:
            'When a student connects Stratia to Claude or ChatGPT: Stratia\'s sign-in page '
            + '(Continue with Google, or a 6-digit code by email), then a screen naming the '
            + 'signed-in account and what the assistant can read, change and never do, on every '
            + 'new connection. Just us shows it to listed accounts (and the browser they signed '
            + 'in from). Off: Google\'s chooser, as today.',
    },
    // Turn Shorts views into site visits (#5267, founder pick B). Desk only:
    // Just us shows the Desk's new pieces; Everyone builds new Shorts with
    // the profile close. Retire once on.
    {
        key: 'shorts_link_in_profile',
        title: 'Shorts: ask for the tap (Link in our profile)',
        description:
            'The closing frame says Link in our profile, with the typed address as the backup, '
            + 'coming up with the closing line; the Instagram caption says Link in bio; the pinned '
            + 'YouTube comment points at the channel page. Desk only: no student sees it. Just us: '
            + 'the Desk shows the profile-links checklist, the day\'s Story link and the Related '
            + 'video lines. Everyone: new Shorts build with the new close. Off: none of it.',
    },
    // The Desk's goal loop (#5307, founder pick A). Desk only: Just us is the
    // shadow week (the goal strip and its cards show; the Top 3, the value
    // score and the noise cut are computed, not shown); Everyone turns them
    // on. Retire once on.
    {
        key: 'desk_goal_loop',
        title: 'Desk: the goal on Today (paying customers first)',
        description:
            'The goal on Today: this month\'s paying customers against the goal of record, the six '
            + 'steps from a visit to a purchase with the leak marked, and one goal card a month: a step '
            + 'proven a leak, or the month at 0 on the 15th. Desk only: no student sees it. Just us: the goal '
            + 'strip and those cards, while the value score, the Top 3 and the noise cut are computed '
            + 'in shadow. Everyone: the Top 3 shows, the score orders your cards and the watch signals '
            + 'fold into one row a day. Off: none of it.',
    },
    // The Desk fixes its own failing cards (#5515). Just us: the pilot (draft
    // and build cards); Everyone: every card it may read; off: nothing runs.
    {
        key: 'desk_self_heal',
        title: 'Desk: it fixes its own failing cards',
        description:
            'The Desk fixes its own failing cards: a card that failed on a defect (a guard stop, a '
            + 'refused step, a crash, the same failure twice) opens one fix job: a gated PR (a code '
            + 'change is yours to merge), then the Desk deploys it and runs the card again, ending in '
            + 'a Fixed and finished row. Desk only: no student sees it. Just us: the pilot, cards whose work ends '
            + 'at a draft or a build (the Short builds and season drafts). Everyone: every card it may '
            + 'read (a post or a send comes back to you fixed, with Retry). Off: nothing runs; failed '
            + 'cards stay as they are.',
    },
    // Views by day on the Desk (#5545, founder pick A). Desk only: /desk-posts
    // carries the chart while it is on. Retire once on.
    {
        key: 'desk_views_by_day',
        title: 'Desk: views by day',
        description:
            'Views by day on Growth, Posts and channels: one chart of every channel\'s video views '
            + 'to date by day, with a marker at each 50k the total passes, and the views each day '
            + 'added by channel beneath, under four numbers (to date, yesterday, the 7-day pace, the '
            + 'next milestone). It counts exactly what the landing page\'s milestone ribbon counts. '
            + 'Desk only: no student sees it. Just us and Everyone both show it on the Desk. Off: the '
            + 'Posts view as before.',
    },
    // The essay review reads the prompt's form and limit (#5336, founder pick
    // C). A rollout gate: profile-manager-v2's essay-feedback route reads it
    // server-side; EssayHelpPage reads it for the editor's counter. Retire
    // once on for everyone.
    {
        key: 'essay_review_prompt_form',
        title: 'Essay review: list prompts as lists, the word limit stated',
        description:
            'A list prompt (Columbia\'s texts and outlets, Stanford\'s five things) is reviewed as a '
            + 'list and never asked to describe. Every review opens with the draft\'s words against the '
            + 'prompt\'s limit, worked out in code ("118 words, 18 over ... 100-word limit"), and the '
            + 'editor\'s counter reads "118 / 100 words" while the student types. Just us = internal '
            + 'and demo accounts. Off: today\'s review and counter.',
    },
    // Our most-watched Shorts on /videos (#5433, founder pick B). Signed out
    // and prerendered, like landing_story: only Everyone reaches visitors,
    // and going live needs a frontend deploy; the team previews with
    // ?videos_preview=1. Retire once on.
    {
        key: 'videos_top_shorts',
        title: 'Videos page: our most-watched Shorts',
        description:
            'A ranked list of the ten Shorts with the most views on Instagram, TikTok and YouTube '
            + 'together, each with its own count per channel, dated; a tap plays the Short full screen '
            + 'on our site, swipe up for the next. The landing ribbon\'s Watch them lands on the list. '
            + 'Only Everyone reaches visitors and it needs a frontend deploy; preview with '
            + '?videos_preview=1 on a team device. Off: /videos as before.',
    },
    // What families like yours paid, per school (#5492, founder pick B of
    // the net-cost mocks, 2026-10-26). A rollout gate: Just us = internal and
    // demo accounts. Retire once it is on for everyone.
    {
        key: 'net_cost_estimate',
        title: 'Financial Aid: what families like yours paid, per school',
        description:
            'The family answers five questions once on /financial-aid and sees its federal aid index '
            + 'and Pell estimate free. The cost chart gains a "Your family" layer: for each school on '
            + 'the list, the range of what aided families in its income range paid there on average, '
            + 'the federal years named, an honest "no estimate here" where the average does not apply, '
            + 'and the school\'s own calculator one tap away. The ranges are the paid action; a '
            + 'correction within 24 hours is free. Off: the page as before.',
    },
    // One free read, then the price (#5631, founder yes 2026-10-27, mock A
    // of #5621). Read once per account at creation (the server decides from
    // the account's own record); accounts made before are grandfathered.
    // Retire once it is on for everyone.
    {
        key: 'one_free_read',
        title: 'One free read, then the price',
        description:
            'A new account made while this is on for it gets one full college read free and no '
            + '7-day trial. Its second full read (or Major Map, essay outline or review, or the '
            + 'whole list\'s cost ranges) opens one sheet: the first school named, the Season Pass '
            + 'at its price, the refund condition, Not now. The second school stays on the list '
            + 'with its odds. Accounts made before keep today\'s free reads and trial. Just us: '
            + 'internal and demo accounts created from now on. Off: today\'s grant.',
    },
];

// The flag that moves the Admin pages into Operations (#5186). One name for
// the nav, the redirects and the Operations page.
export const ADMIN_UNDER_OPERATIONS = 'admin_under_operations';

// Popular questions inside Astra (#5258): one name for the nav, the Playbook
// redirect, the Connect prompts and signed-in Astra's shelf.
export const ASTRA_POPULAR_QUESTIONS = 'astra_popular_questions';
// The essay review by prompt form (#5336): the page's counter reads it.
export const ESSAY_REVIEW_PROMPT_FORM = 'essay_review_prompt_form';

export const FLAG_MODES = [
    { value: 'off', label: 'Off', hint: 'Nobody sees it' },
    { value: 'allowlist', label: 'Just us', hint: 'Only the specific emails you list' },
    { value: 'rules', label: 'Rules', hint: 'Target by rollout %, subscribers, or cohort' },
    { value: 'on', label: 'Everyone', hint: 'Live for all users' },
];

// Rule attributes for the builder — mirrors ATTRIBUTES in flag_rules.py. The
// backend is authoritative (it rejects anything not declared there); this only
// drives which inputs the builder shows.
export const RULE_ATTRIBUTES = [
    { key: 'percentage', label: 'Percentage rollout', type: 'percentage',
        help: 'Enable for a stable % of users — raising it only ever adds people.' },
    { key: 'subscriber', label: 'Subscriber', type: 'boolean' },
    { key: 'email_domain', label: 'Email domain', type: 'string',
        operators: ['eq', 'in'], placeholder: 'stratiaadmissions.com' },
    { key: 'graduation_year', label: 'Graduation year', type: 'number',
        operators: ['eq', 'in', 'lte', 'gte'], placeholder: '2027' },
    { key: 'grade', label: 'Grade', type: 'string',
        operators: ['eq', 'in'], placeholder: '12th Grade' },
];

export const OPERATOR_LABELS = {
    eq: 'is', in: 'is one of', lte: 'is at most', gte: 'is at least',
    lt: 'under', is_true: 'yes', is_false: 'no',
};

const _attrType = (key) => RULE_ATTRIBUTES.find((a) => a.key === key)?.type;

// Backend rule set → editable builder draft. An "is one of" array becomes the
// raw comma text the input edits; everything else passes through.
export function rulesToDraft(rules) {
    return {
        match: rules?.match || 'any',
        conditions: (rules?.conditions || []).map((c) => ({
            attribute: c.attribute,
            operator: c.operator,
            value: Array.isArray(c.value) ? c.value.join(', ') : (c.value ?? ''),
        })),
    };
}

// Builder draft → backend wire shape (comma text → array; numbers coerced).
export function rulesToWire(draft) {
    return {
        match: draft?.match || 'any',
        conditions: (draft?.conditions || []).map((c) => {
            if (c.operator === 'is_true' || c.operator === 'is_false') {
                return { attribute: c.attribute, operator: c.operator };
            }
            if (c.operator === 'in') {
                const parts = String(c.value ?? '').split(',').map((s) => s.trim()).filter(Boolean);
                return {
                    attribute: c.attribute, operator: 'in',
                    value: _attrType(c.attribute) === 'number' ? parts.map(Number) : parts,
                };
            }
            if (_attrType(c.attribute) === 'percentage') {
                return { attribute: c.attribute, operator: 'lt', value: Number(c.value) || 0 };
            }
            if (_attrType(c.attribute) === 'number') {
                return { attribute: c.attribute, operator: c.operator, value: Number(c.value) };
            }
            return { attribute: c.attribute, operator: c.operator, value: String(c.value ?? '').trim() };
        }),
    };
}
